import { useEffect, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useTranslate } from 'react-polyglot';
import type { ApexOptions } from 'apexcharts';
import { useTheme } from 'styled-components';

import {
  ChartWrapper,
  Legend,
  LegendsWrapper,
  LegendWrapper,
  Marker,
  QuestionSubtitle,
  Wrapper,
} from './QuestionPieChart.styled';

/**
 * The chart is as tall as a share of the width it is given, so it grows with the panel
 * instead of being pinned to a number. Measured rather than asked for as a `100%` height:
 * a percentage resolves against the box that *contains* the chart, so every redraw made
 * that box taller and the next redraw taller still.
 *
 * Full screen hands the chart a far wider box than the side panel does, so there it takes
 * a smaller share of it: the same ratio would be a pie the height of the whole stage.
 */
const HEIGHT_RATIO = 1;
const EXPANDED_HEIGHT_RATIO = 0.6;

/** Stands in for the single frame before the container has been measured. */
const FALLBACK_HEIGHT = 200;

interface QuestionPieChartProps {
  correct: number;
  incorrect: number;
  unanswered: number;
  questionText: string;
  /** Full screen draws the same chart in a wider box, at a flatter ratio. */
  isExpanded?: boolean;
}

/**
 * Hits, errors and silence, adding up to the whole room.
 *
 * ponytail: a near copy of panel's `ScorableQuestionSummary` — same three slices, same
 * palette, same white pills. Upgrade path is extracting it to `@genially/ui`, which both
 * apps already depend on, with the three counts and the three labels as props. Copied
 * rather than extracted here so a play ticket does not reshape another app's analytics
 * page; the duplication is how the chart is painted, never how the numbers are computed.
 */
export const QuestionPieChart = ({
  correct,
  incorrect,
  unanswered,
  questionText,
  isExpanded = false,
}: QuestionPieChartProps) => {
  const t = useTranslate();
  const { color } = useTheme();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(FALLBACK_HEIGHT);

  // ApexCharts measures its container when it mounts and afterwards only redraws on a
  // window resize. The panel animates its own width in and out of full screen, so a chart
  // mounted mid-animation keeps the width the panel was leaving behind: a thumbnail on the
  // big stage, and wider than the side panel it comes back to, which is why it looked like
  // it had not rendered at all. Measuring the container ourselves and handing the chart a
  // new height is what makes it redraw — at that height and at the width it now has.
  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return undefined;

    const observer = new ResizeObserver(([{ contentRect }]) => {
      // Off the width alone. A redraw changes the height of this same box, so answering a
      // height change with another redraw is a chart that grows without ever stopping.
      setHeight(contentRect.width * (isExpanded ? EXPANDED_HEIGHT_RATIO : HEIGHT_RATIO));
    });

    observer.observe(wrapper);

    return () => observer.disconnect();
  }, [isExpanded]);

  const slices = [
    {
      label: t('__new.play.analyticsResults.question.correct'),
      value: correct,
      color: color.background.graphics(7),
    },
    {
      label: t('__new.play.analyticsResults.question.incorrect'),
      value: incorrect,
      color: color.background.graphics(9),
    },
    {
      label: t('__new.play.analyticsResults.question.unanswered'),
      value: unanswered,
      color: color.background.graphics(3),
    },
  ];

  // A session with nobody in it has no slices at all: a flat grey circle says "nothing to
  // read here" without pretending the question went one way or the other.
  const activeSlices = slices.filter(slice => slice.value > 0);
  const hasData = activeSlices.length > 0;

  const options: ApexOptions = {
    plotOptions: { pie: { expandOnClick: false, dataLabels: { offset: -20 } } },
    labels: hasData ? activeSlices.map(slice => slice.label) : [''],
    colors: hasData
      ? activeSlices.map(slice => slice.color)
      : [color.content.reversed.default(0.1)],
    legend: { show: false },
    stroke: { show: false },
    dataLabels: {
      enabled: hasData,
      textAnchor: 'middle',
      style: {
        colors: [color.background.reversed.default(0.5)],
        fontSize: isExpanded ? '15px' : '12px',
        fontFamily: 'Schibsted Grotesk',
        fontWeight: '400',
      },
      background: {
        enabled: true,
        foreColor: color.content.reversed.default(),
        dropShadow: { enabled: false },
        borderWidth: 0,
      },
      dropShadow: { enabled: false },
      formatter: (value: number) => `${Math.round(value * 10) / 10}%`,
    },
    tooltip: { enabled: false },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
  };

  return (
    <Wrapper>
      <ChartWrapper ref={wrapperRef}>
        <QuestionSubtitle>{questionText}</QuestionSubtitle>

        <ReactApexChart
          type="pie"
          options={options}
          series={hasData ? activeSlices.map(slice => slice.value) : [1]}
          width="100%"
          height={height}
        />
      </ChartWrapper>

      <LegendsWrapper>
        {slices.map(slice => (
          <LegendWrapper key={slice.label}>
            <Marker $color={slice.color} />
            {/* Plain text, not a translated string: it is a number and a label in every
                language, and this way the tests can assert on it. */}
            <Legend>{`${slice.value} ${slice.label}`}</Legend>
          </LegendWrapper>
        ))}
      </LegendsWrapper>
    </Wrapper>
  );
};
